import { useState } from 'react';
import { Check, X } from 'lucide-react';
import type { Vinculo } from './tipos';

export type DadosDoVinculo = {
  fornecedor_id: number;
  descricao_fornecedor: string;
  codigo_fornecedor: string;
};

type Props = {
  fornecedores: { id: number; apelido: string }[];
  vinculo?: Vinculo;
  fornecedorId?: number;
  ocupado: boolean;
  erro: string | null;
  aoSalvar: (dados: DadosDoVinculo) => void;
  aoCancelar: () => void;
};

/**
 * Serve para as duas coisas: corrigir o que o sistema aprendeu e ensinar de
 * véspera o que o comprador já sabe de cabeça.
 *
 * O produto não se escolhe aqui — o vínculo pertence ao produto que está aberto.
 * Quando o sistema ligou o cabo ao produto errado, o caminho é remover e criar
 * no produto certo, que é o que o comprador faz quando está com o PDF na tela.
 */
export default function FormularioVinculo({
  fornecedores,
  vinculo,
  fornecedorId,
  ocupado,
  erro,
  aoSalvar,
  aoCancelar,
}: Props) {
  const [form, setForm] = useState({
    fornecedor_id: fornecedorId ?? fornecedores[0]?.id ?? 0,
    descricao_fornecedor: vinculo?.descricao_fornecedor ?? '',
    codigo_fornecedor: vinculo?.codigo_fornecedor ?? '',
  });

  return (
    <form
      className="mt-sm rounded-md border border-border bg-dominant p-sm"
      onSubmit={(evento) => {
        evento.preventDefault();
        aoSalvar(form);
      }}
    >
      <div className="flex flex-wrap items-end gap-sm">
        {fornecedorId === undefined && (
          <label className="text-[12px] text-ink-soft">
            <span className="mb-[2px] block font-bold">Fornecedor</span>
            <select
              value={form.fornecedor_id}
              onChange={(evento) => setForm({ ...form, fornecedor_id: Number(evento.target.value) })}
              className="rounded-md border border-border bg-surface px-sm py-xs text-[13px] text-ink focus:border-accent focus:outline-none"
            >
              {fornecedores.map((fornecedor) => (
                <option key={fornecedor.id} value={fornecedor.id}>
                  {fornecedor.apelido}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="min-w-[240px] flex-1 text-[12px] text-ink-soft">
          <span className="mb-[2px] block font-bold">Como o fornecedor escreve</span>
          <input
            type="text"
            required
            maxLength={255}
            value={form.descricao_fornecedor}
            onChange={(evento) => setForm({ ...form, descricao_fornecedor: evento.target.value })}
            placeholder="Cabo FlexSil 750 V 4,00 Preto"
            className="w-full rounded-md border border-border bg-surface px-sm py-xs text-[13px] text-ink placeholder:text-muted focus:border-accent focus:outline-none"
          />
        </label>

        <label className="text-[12px] text-ink-soft">
          <span className="mb-[2px] block font-bold">Código dele</span>
          <input
            type="text"
            maxLength={40}
            value={form.codigo_fornecedor}
            onChange={(evento) => setForm({ ...form, codigo_fornecedor: evento.target.value })}
            placeholder="opcional"
            className="w-[140px] rounded-md border border-border bg-surface px-sm py-xs text-[13px] text-ink placeholder:text-muted focus:border-accent focus:outline-none"
          />
        </label>

        <div className="flex items-center gap-xs">
          <button
            type="submit"
            disabled={ocupado || !form.descricao_fornecedor.trim()}
            className="inline-flex items-center gap-xs rounded-md bg-faixa px-sm py-xs text-[12px] font-bold text-white disabled:opacity-40"
          >
            <Check size={13} aria-hidden="true" />
            Salvar
          </button>
          <button
            type="button"
            onClick={aoCancelar}
            className="inline-flex items-center gap-xs rounded-md border border-border bg-surface px-sm py-xs text-[12px] font-bold text-ink-soft hover:border-accent"
          >
            <X size={13} aria-hidden="true" />
            Cancelar
          </button>
        </div>
      </div>

      {erro && <p className="mt-sm text-[12px] text-danger">{erro}</p>}
    </form>
  );
}
